/**
 * markdownNormalize — shared pre-parse fixups for model-produced markdown.
 *
 * Extracted from DraftTab so every ReactMarkdown surface (draft preview,
 * Action History snippets/traces, citation excerpts) applies the same two
 * fixups before handing text to remark. Without them, drafted tables render
 * as literal pipes and JSON-stringified drafts render as one giant line.
 */

/**
 * Decode escape sequences that arrive *literally* in draft_content. Some draft
 * payloads are JSON-stringified upstream, so a real newline reaches us as the
 * two characters "\" + "n" (and tabs as "\" + "t"). React-markdown then sees one
 * giant line and renders no lists, headings, or line breaks — the markdown
 * structure is invisible. We convert those literal escapes back to real
 * characters before parsing.
 *
 * Conservative by design: only runs when a literal backslash is present, and
 * only decodes whitespace escapes (\r\n, \n, \r, \t) plus an escaped backslash
 * ("\\" → "\"). Prose virtually never contains a bare "\n" token, so the risk
 * of mangling real text (e.g. a Windows path) is negligible for CSR drafts.
 */
export function decodeEscapedWhitespace(text: string): string {
  if (!text.includes("\\")) return text;
  return text
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\\\/g, "\\");
}

/** A GFM table delimiter row, e.g. `|---|:--:|---|` (the line under the header). */
const TABLE_DELIMITER_RE = /^\s*\|?\s*:?-{1,}:?\s*(?:\|\s*:?-{1,}:?\s*)*\|?\s*$/;
const hasPipe = (line: string): boolean => line.includes("|");

/**
 * Ensure a blank line sits before and after every GFM table block.
 *
 * The drafting model emits markdown with single "\n" between blocks, but a GFM
 * table can NOT interrupt a paragraph — without a preceding blank line, remark
 * folds the bold label + the `| … |` rows into one paragraph and renders the
 * pipes as literal text (no <table>). The Word writer is lenient and renders the
 * table fine, so the gap only showed in the add-in. We insert the missing blank
 * lines around each table while keeping the table's own rows contiguous (a blank
 * line *between* rows would end the table). Idempotent.
 */
export function normalizeTableSpacing(md: string): string {
  const lines = md.split("\n");
  const out: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const next = lines[i + 1] ?? "";
    const isTableStart = hasPipe(line) && !TABLE_DELIMITER_RE.test(line) && TABLE_DELIMITER_RE.test(next);

    if (!isTableStart) {
      out.push(line);
      continue;
    }

    // Blank line before the table (unless one is already there).
    if (out.length > 0 && out[out.length - 1].trim() !== "") out.push("");
    out.push(line, next); // header + delimiter
    let j = i + 2;
    while (j < lines.length && lines[j].trim() !== "" && hasPipe(lines[j])) {
      out.push(lines[j]);
      j += 1;
    }
    i = j - 1;
    // Blank line after the table (unless we hit EOF or an existing blank).
    if (i + 1 < lines.length && (lines[i + 1] ?? "").trim() !== "") out.push("");
  }
  return out.join("\n");
}
